import { PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Duration } from "luxon";
import React, { useRef, useState } from "react";

import { Label } from "@fronterahealth/frontera-ui-components";

import { EmojiButtonInterface } from "@pages/VideoSessionsV2/types";

interface EmojiCommentCardProps {
  selectedEmoji: EmojiButtonInterface | null;
  currentTime: number;
  onSubmit: (comment: string, timestamp: number) => void;
  onClose: () => void;
  className?: string;
  isCommentSaving: boolean;
  containerClassName?: string;
}

export const EmojiCommentCard: React.FC<EmojiCommentCardProps> = ({
  selectedEmoji,
  currentTime,
  onSubmit,
  onClose,
  className,
  isCommentSaving = false,
  containerClassName,
}) => {
  const [comment, setComment] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    onSubmit(comment, currentTime);
    setComment("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  };

  if (!selectedEmoji) return null;

  return (
    <div className={clsx("p-2 border-b border-limestone-200", containerClassName)}>
      <form
        onSubmit={handleSubmit}
        className={clsx(
          `flex items-center w-full border  rounded-lg px-3 py-1 bg-white shadow-xs 
              focus-within:border-2 focus-within:border-green-600`,
          className,
        )}
      >
        {/* Input field */}
        <textarea
          ref={textareaRef}
          rows={1}
          placeholder="Leave a comment..."
          className="flex-1 resize-none border-none outline-hidden focus:outline-hidden focus:ring-0 text-sm text-gray-700 py-1 pr-2"
          value={comment}
          autoFocus
          onChange={handleInputChange}
        />

        {/* Timestamp */}
        <Label size="extra-small" className="mr-2">
          Comment at {Duration.fromObject({ seconds: currentTime }).toFormat("hh:mm:ss")}
        </Label>

        {/* Send button */}
        <button
          type="submit"
          disabled={isCommentSaving}
          className="text-green-600 hover:text-green-700 disabled:opacity-50"
        >
          <PaperAirplaneIcon className="h-5 w-5" />
        </button>

        {/* Optional close (X) */}
        <button type="button" onClick={onClose} className="ml-2 text-gray-400 hover:text-gray-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
};
